import { Button, ChevronRightIcon, Chip, EmptyState, FriendlyError, SectionLoading } from "@codel/dova-ds";
import { useNavigate } from "react-router-dom";
import { useReports, type ReportListItem } from "../hooks/reportHooks";
import { formatReportDate } from "./reportFormatting";
import { REPORTS_PAGE_COPY } from "./reportsCopy";
import { BackButton } from "../components/BackButton";

function ReportRow({ report, featured = false }: { report: ReportListItem; featured?: boolean }) {
  const navigate = useNavigate();
  const week = REPORTS_PAGE_COPY.weekOf(formatReportDate(report.weekStart));
  return (
    <button
      type="button"
      onClick={() => navigate(`/me/reports/${report.uuid}`)}
      aria-label={`${report.title} ${week}`}
      className={[
        "flex w-full items-center justify-between gap-4 text-left transition hover:bg-[var(--canvas)]",
        "focus:outline-none focus:ring-2 focus:ring-[var(--interactive)] focus:ring-offset-2",
        featured ? "px-4 py-4" : "border-t border-[var(--border)] px-4 py-4",
      ].join(" ")}
    >
      <span className="flex min-w-0 flex-col">
        <span className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-secondary)]">
          {week}
        </span>
        <span
          className={[
            "mt-1 truncate font-serif font-medium leading-tight text-[var(--text-primary)]",
            featured ? "text-xl" : "text-lg",
          ].join(" ")}
        >
          {report.title}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {report.isNew && (
          <Chip role="success" variant="solid">
            {REPORTS_PAGE_COPY.newChip}
          </Chip>
        )}
        <ChevronRightIcon className="text-[var(--text-tertiary)]" />
      </span>
    </button>
  );
}

export function ReportsPage() {
  const reports = useReports();
  const navigate = useNavigate();

  if (reports.isLoading) {
    return (
      <main className="px-6 py-12">
        <SectionLoading label={REPORTS_PAGE_COPY.loadingLabel} />
      </main>
    );
  }

  if (reports.error) {
    return (
      <main className="px-6 py-12">
        <FriendlyError detail={reports.error.message} />
      </main>
    );
  }

  if (!reports.data || reports.data.length === 0) {
    return (
      <main className="px-6 py-12">
        <EmptyState
          eyebrow={REPORTS_PAGE_COPY.eyebrow}
          title={REPORTS_PAGE_COPY.emptyTitle}
          body={REPORTS_PAGE_COPY.emptyBody}
        />
      </main>
    );
  }

  const [featured, ...olderReports] = reports.data;

  return (
    <main className="flex min-h-screen flex-col gap-6 px-6 py-8">
      <header className="flex flex-col gap-4">
        <BackButton label={REPORTS_PAGE_COPY.backLabel} onClick={() => navigate("/me/home")} />
        <div>
          <h1 className="m-0 font-serif text-2xl font-medium leading-tight text-[var(--text-primary)]">
            {REPORTS_PAGE_COPY.title}
          </h1>
          <p className="mt-2 [font:var(--type-body)] text-[var(--text-secondary)]">
            {REPORTS_PAGE_COPY.sub}
          </p>
        </div>
      </header>
      <section className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        <ReportRow report={featured} featured />
        {olderReports.map((report) => (
          <ReportRow key={report.uuid} report={report} />
        ))}
      </section>
      {reports.hasMore ? (
        <Button variant="outline" size="xl" onClick={reports.loadMore} disabled={reports.isLoadingMore}>
          {reports.isLoadingMore ? REPORTS_PAGE_COPY.loadingMoreLabel : REPORTS_PAGE_COPY.loadMoreLabel}
        </Button>
      ) : null}
    </main>
  );
}
